// status-progress-types.ts
//
// Progress panel (CLI 3.41, plan 20261020-1117 phase-01): the shape of
// `status --json` `payload.progress` — the one block the VSCode extension's
// Autopilot panel reads (journey · tickets · current attempt · human actions).
// snake_case like the rest of `status --json`. Evolution rule (plan §Hợp đồng
// B13): a new optional field / enum value does NOT bump `schema_version`;
// a changed meaning/type or a removed field does. Zod twin:
// schemas/status-progress-schema.ts. beta.2 (dogfood DF-P4-1/2/5) added the
// `review` step, `supervisor_dead`, `hint_chat` and `controller_reserved` —
// all additive, so still schema_version 1.

export const PROGRESS_SCHEMA_VERSION = 1;

export const JOURNEY_STEP_IDS = [
  "g0",
  "g1",
  "survey",
  "plan",
  "g2",
  "delivering",
  /** Controller-reserved review candidates (T-9001/T-9002) — runs with or without release config. */
  "review",
  "integration",
  "g3",
  "release",
] as const;
export type JourneyStepId = (typeof JOURNEY_STEP_IDS)[number];

export const JOURNEY_STATES = [
  "done",
  "active",
  "waiting_human",
  "blocked",
  "pending",
  "skipped",
  "unknown",
] as const;
export type JourneyState = (typeof JOURNEY_STATES)[number];

export interface JourneyStep {
  id: JourneyStepId;
  state: JourneyState;
  started_at?: string;
  ended_at?: string;
  summary?: string;
  /** What decided `state`, e.g. `gate:g2`, `tickets:survey`, `label:waiting_ci`. */
  source: string;
}

export interface ProgressAttempt {
  attempt_no: number;
  run_id: string;
  /** Absent on attempts journaled before 3.41 (no `sessionId` on attempt_started). */
  session_id?: string;
  started_at?: string;
  finished_at?: string;
  category?: string;
}

export interface ProgressTicket {
  id: string;
  title: string;
  kind: string;
  slice_id: string;
  status: string;
  depends_on: string[];
  adapter: string;
  executor: string;
  agent_hints: string[];
  skill_hints: string[];
  playbook?: string;
  /** Finished physical runs (attempt-state.json) — the in-flight run is `current_attempt.attempt_no`. */
  physical_runs: number;
  counted_failures: number;
  /** Ceiling of COUNTED failures (not of runs); absent when effective-config is unreadable. */
  max_counted_failures?: number;
  started_at?: string;
  finished_at?: string;
  cost_usd?: number;
  last_verdict_category?: string;
  last_verdict_reasons: string[];
  /** Orchestrator recovery: the source ticket this controller-written recovery ticket unblocks (ledger). */
  recovery_of?: string;
  /** A controller-owned session (review candidate T-9001/T-9002) with no `.avatar/tickets` file. */
  controller_reserved?: true;
  attempts: ProgressAttempt[];
}

export const WORKER_STATES = ["worker_running", "supervisor_working", "dead"] as const;
export type WorkerState = (typeof WORKER_STATES)[number];

export interface ProgressCurrentAttempt {
  ticket_id: string;
  /** `attemptNo` of the open attempt_started (= physical_runs + 1). */
  attempt_no: number;
  run_id: string;
  session_id?: string;
  started_at?: string;
  executor: string;
  worker_pid?: number;
  worker_state: WorkerState;
}

export const HUMAN_ACTION_KINDS = [
  "gate",
  "decision",
  "interview",
  "cost_ceiling",
  "escalation",
  /** Supervisor died mid-mission (orphan machine lock or work in flight) — resume it. */
  "supervisor_dead",
] as const;
export type HumanActionKind = (typeof HUMAN_ACTION_KINDS)[number];

export interface ProgressHumanAction {
  kind: HumanActionKind;
  gate?: string;
  request_id?: string;
  question_id?: string;
  ticket_id?: string;
  blocked_by?: string;
  recovery_skipped_reason?: string;
  text: string;
  /** The sentence the operator says to Claude in chat (no "Nói với Claude" prefix). */
  hint_chat?: string;
  /** Equivalent CLI command (syntax from the real command registry); may chain with ` && `. */
  hint_cli?: string;
}

export interface ProgressMission {
  id: string;
  /** recorded_at of the journal's FIRST record — absent on journals older than 3.41. */
  started_at?: string;
  /** Same label as `payload.status` (pause overlay included); absent when none was journaled. */
  status_label?: string;
  /** True when the label is a stop/wait label that later work superseded (phase-01 "nhãn cũ"). */
  status_label_stale?: boolean;
  supervisor_alive: boolean;
  terminal: boolean;
  /** First journey step not done/skipped; absent when every step is. */
  active_stage?: JourneyStepId;
}

export interface StatusProgress {
  schema_version: typeof PROGRESS_SCHEMA_VERSION;
  generated_at: string;
  mission: ProgressMission;
  journey: JourneyStep[];
  tickets: ProgressTicket[];
  /** `.avatar/tickets` could not be loaded (e.g. `duplicate_ticket_id`) — `tickets` is then []. */
  tickets_error?: string;
  current_attempt?: ProgressCurrentAttempt;
  human_actions: ProgressHumanAction[];
}
